import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, GraduationCap, Wrench, HardHat, Clock, PlayCircle, Zap, Droplets, Wind, ClipboardList, HardDrive, Leaf } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import { coursesApi } from "@/lib/api";
import { formatDuration } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PageSEO from "@/components/seo/PageSEO";
import { SITE_BASE_URL, ORG_ID, postalAddress } from "@/components/seo/schemaHelpers";
import { COMPANY } from "@/lib/company";

const CATEGORY_ICONS = {
    "Safety": HardHat,
    "Health & Safety": HardHat,
    "Quality Control": ClipboardList,
    "Equipment Operation": HardDrive,
    "Sustainable Construction": Leaf,
    "HVAC": Wind,
    "Plumbing": Droplets,
    "Electrical": Zap,
    "Site Management": Wrench,
    "Certifications": ShieldCheck,
    "Skills": GraduationCap,
};

const GRADUATE_TESTIMONIALS = [
    {
        name: "Callum Hughes",
        role: "Site Supervisor · ElevateBuild",
        quote:
            "The programme module gave me a language for what I was doing on instinct. My PM noticed the difference in a fortnight.",
    },
    {
        name: "Ade Fashola",
        role: "Steel Fixer · Bridgewater Civils",
        quote:
            "Passed my CSCS on the first sit. The dust and noise section is exactly what came up.",
    },
    {
        name: "Priya Sinha",
        role: "Graduate Engineer · ElevateBuild",
        quote:
            "The BIM module actually explains why we do what we do — not just how to click the buttons.",
    },
];

export default function PortalLandingPage() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        coursesApi
            .list()
            .then(setCourses)
            .catch(() => setCourses([]))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(courses.map((c) => c.category));
        return Array.from(set).sort();
    }, [courses]);

    return (
        <div
            className="relative isolate bg-brand-charcoal text-brand-offwhite"
            data-testid="portal-page"
        >
            <PageSEO
                title="Training Portal — EB Construction Academy | UK Construction Learning"
                description="Accredited construction training portal by EB Construction. Structured pathways in health & safety, CSCS certifications and trade skills with tracked progress and publicly verifiable certificates."
                canonicalPath="/portal"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "EducationalOrganization",
                        "@id": `${SITE_BASE_URL}/portal#educationalOrganization`,
                        name: "EB Construction Training Academy",
                        parentOrganization: { "@id": ORG_ID },
                        url: `${SITE_BASE_URL}/portal`,
                        logo: `${SITE_BASE_URL}/brand/logo-primary.png?v=3`,
                        description:
                            "Accredited UK construction learning platform for teams, subcontractors and the wider industry — health & safety, CSCS, plant operation, HVAC, electrical, plumbing, and site management.",
                        email: COMPANY.email,
                        telephone: COMPANY.phoneE164,
                        address: postalAddress(),
                        areaServed: {
                            "@type": "Country",
                            name: "United Kingdom",
                        },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                name: "Home",
                                item: SITE_BASE_URL,
                            },
                            {
                                "@type": "ListItem",
                                position: 2,
                                name: "Training Portal",
                                item: `${SITE_BASE_URL}/portal`,
                            },
                        ],
                    },
                    ...(courses.length
                        ? [
                              {
                                  "@context": "https://schema.org",
                                  "@type": "ItemList",
                                  name: "EB Construction Featured Courses",
                                  itemListElement: courses.slice(0, 6).map((c, i) => ({
                                      "@type": "ListItem",
                                      position: i + 1,
                                      url: `${SITE_BASE_URL}/portal/courses/${c.slug}`,
                                      name: c.title,
                                  })),
                              },
                          ]
                        : []),
                ]}
            />
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-brand-linelight">
                <div className="pointer-events-none absolute inset-0 -z-10 opacity-30">
                    <img
                        src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2000&q=80"
                        alt=""
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/70 via-brand-charcoal/85 to-brand-charcoal" />
                </div>
                <div className="container-x grid gap-12 py-24 md:grid-cols-12 md:py-32">
                    <div className="md:col-span-8">
                        <Reveal>
                            <img
                                src="/brand/logo-horizontal-dark.png?v=3"
                                alt="EB Construction Training Academy"
                                className="mb-6 h-12 w-auto md:h-14"
                                data-testid="portal-landing-logo"
                            />
                            <SectionLabel index={0} label="Training Portal" tone="light" />
                            <h1 className="mt-6 font-display text-6xl leading-[1.02] md:text-9xl">
                                LEARN THE <br />
                                <span className="text-brand-gold">CRAFT.</span> BUILD THE CAREER.
                            </h1>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <p className="mt-6 max-w-xl font-body text-lg italic text-brand-gold md:text-xl">
                                Built on Trust. Designed for Life.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-brand-offwhite/85 md:text-xl">
                                An accredited construction learning platform for our teams,
                                subcontractors and the wider industry. Structured pathways in
                                safety, certifications and skills — with progress tracking, video
                                lessons, and publicly verifiable certificates on completion.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                {isAuthenticated ? (
                                    <>
                                        <Link
                                            to="/portal/dashboard"
                                            className="btn-primary"
                                            data-testid="portal-cta-dashboard"
                                        >
                                            My Dashboard
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                        <Link
                                            to="/portal/courses"
                                            className="btn-ghost-dark"
                                            data-testid="portal-cta-catalog"
                                        >
                                            Browse Courses
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/portal/signup"
                                            className="btn-primary"
                                            data-testid="portal-cta-signup"
                                        >
                                            Get started — Sign up
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                        <Link
                                            to="/portal/login"
                                            className="btn-ghost-dark"
                                            data-testid="portal-cta-login"
                                        >
                                            Log in
                                        </Link>
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4">
                        <Reveal delay={0.4}>
                            <div className="border border-brand-linelight bg-brand-charcoal/70 p-8 backdrop-blur">
                                <p className="font-mono-tag text-brand-gold">
                                    [ Phase 2 · Live ]
                                </p>
                                <p className="mt-4 font-display text-3xl leading-tight">
                                    {isAuthenticated
                                        ? `WELCOME BACK, ${(user?.full_name || "").split(" ")[0]?.toUpperCase() || "TRAINEE"}.`
                                        : "LOGIN, LEARN, PROGRESS."}
                                </p>
                                <p className="mt-4 font-body text-sm leading-relaxed text-brand-offwhite/75">
                                    Course catalogue, video lessons, tracked progress, and
                                    publicly verifiable certificates on completion.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Categories overview */}
            <section className="border-b border-brand-linelight py-20 md:py-28">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={1} label="Categories" tone="light" />
                        <h2 className="mt-6 font-display text-5xl leading-[1.1] md:text-6xl">
                            {categories.length} <span className="text-brand-gold">{categories.length === 1 ? "TRACK" : "TRACKS"}.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 border-t border-l border-brand-linelight sm:grid-cols-2 md:grid-cols-4">
                        {categories.map((cat, i) => {
                            const Icon = CATEGORY_ICONS[cat] || GraduationCap;
                            const count = courses.filter((c) => c.category === cat).length;
                            return (
                                <Reveal
                                    key={cat}
                                    delay={i * 0.08}
                                    className="border-b border-r border-brand-linelight p-8"
                                >
                                    <Link
                                        to={`/portal/courses`}
                                        className="group block"
                                        data-testid={`portal-category-${cat.toLowerCase()}`}
                                    >
                                        <div className="grid h-14 w-14 place-items-center bg-brand-gold text-brand-charcoal">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <h3 className="mt-8 font-display text-2xl font-semibold leading-[1.15]">
                                            {cat}
                                        </h3>
                                        <p className="mt-3 font-mono-tag text-brand-offwhite/60">
                                            {count} course{count === 1 ? "" : "s"}
                                        </p>
                                        <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-offwhite/75 transition-colors group-hover:text-brand-gold">
                                            Explore
                                            <ArrowUpRight className="h-3 w-3" />
                                        </span>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured courses */}
            <section className="border-b border-brand-linelight py-20 md:py-28">
                <div className="container-x">
                    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <div>
                            <Reveal>
                                <SectionLabel index={2} label="Featured Courses" tone="light" />
                                <h2 className="mt-6 font-display text-5xl leading-[1.1] md:text-6xl">
                                    START <span className="text-brand-gold">HERE.</span>
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <Link
                                to="/portal/courses"
                                className="btn-ghost-dark"
                                data-testid="portal-view-all-courses"
                            >
                                View catalog
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>

                    {loading ? (
                        <p className="mt-16 font-mono-tag text-brand-offwhite/60">
                            LOADING COURSES…
                        </p>
                    ) : (
                        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                            {courses.slice(0, 3).map((c, i) => (
                                <Reveal
                                    key={c.id}
                                    delay={i * 0.08}
                                    className="group border border-brand-linelight bg-brand-charcoal/60"
                                >
                                    <Link
                                        to={`/portal/courses/${c.slug}`}
                                        className="block"
                                        data-testid={`portal-featured-course-${c.slug}`}
                                    >
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img
                                                src={c.cover_image}
                                                alt={c.title}
                                                className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="border-t border-brand-linelight p-6">
                                            <div className="flex items-center gap-3">
                                                <span className="border border-brand-gold px-2 py-1 font-mono-tag text-brand-gold">
                                                    {c.category}
                                                </span>
                                                <span className="font-mono-tag text-brand-offwhite/60">
                                                    {c.level}
                                                </span>
                                            </div>
                                            <h3 className="mt-4 font-display text-3xl uppercase leading-tight">
                                                {c.title}
                                            </h3>
                                            <p className="mt-3 font-body text-sm leading-relaxed text-brand-offwhite/75">
                                                {c.short_description}
                                            </p>
                                            <div className="mt-6 flex items-center gap-6 border-t border-brand-linelight pt-4 text-brand-offwhite/70">
                                                <span className="flex items-center gap-2 font-mono-tag">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {formatDuration(c.duration_minutes)}
                                                </span>
                                                <span className="flex items-center gap-2 font-mono-tag">
                                                    <PlayCircle className="h-3.5 w-3.5" />
                                                    {c.lesson_count} lessons
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Graduate testimonials */}
            <section className="py-20 md:py-28">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={3} label="Graduate Voices" tone="light" />
                        <h2 className="mt-6 font-display text-5xl leading-[1.1] md:text-6xl">
                            REAL <span className="text-brand-gold">OUTCOMES.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {GRADUATE_TESTIMONIALS.map((t, i) => (
                            <Reveal
                                key={t.name}
                                delay={i * 0.08}
                                className="border border-brand-linelight bg-brand-charcoal/60 p-8"
                            >
                                <div data-testid={`portal-graduate-${i}`}>
                                    <p className="font-body text-base leading-relaxed text-brand-offwhite/85">
                                        “{t.quote}”
                                    </p>
                                    <div className="mt-6 border-t border-brand-linelight pt-4">
                                        <p className="font-display text-xl uppercase">
                                            {t.name}
                                        </p>
                                        <p className="font-mono-tag text-brand-offwhite/60">
                                            {t.role}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
